import { useCallback, useEffect, useState } from "react";

type LocationId = string | number;
type LocationBulkSelectionItem = Record<string, unknown>;

function useLocationBulkSelection<T extends LocationBulkSelectionItem>(
  locations: T[] = [],
  locationIdKey = "location_id"
) {
  const [showLocationCheckBox, setShowLocationCheckBox] = useState(false);
  const [checkedLocations, setCheckedLocations] = useState<LocationId[]>([]);
  const [disableLocations, setDisableLocations] = useState<LocationId[]>([]);

  useEffect(() => {
    setCheckedLocations((prev) =>
      prev.filter((l) => !disableLocations.includes(l))
    );
  }, [disableLocations]);

  const handleToggleBulkSelection = useCallback((isExpanded: boolean) => {
    setShowLocationCheckBox(isExpanded);
    if (!isExpanded) {
      setCheckedLocations([]);
      setDisableLocations([]);
    }
  }, []);

  const handleToggleGlobalSelection = useCallback(
    (checked: boolean) => {
      if (checked) {
        setCheckedLocations(
          locations
            .map((loc) => loc[locationIdKey] as LocationId)
            .filter((id) => !disableLocations.includes(id)) // don't select locations that are disabled
        );
      } else {
        setCheckedLocations([]);
      }
    },
    [locations, locationIdKey, disableLocations]
  );

  const isAllSelected =
    disableLocations?.length > 0 && disableLocations?.length < locations?.length
      ? checkedLocations?.length ===
        locations?.length - disableLocations?.length
      : locations?.length > 0 && checkedLocations?.length === locations?.length;

  return {
    showLocationCheckBox,
    checkedLocations,
    disableLocations,
    isAllSelected,
    setShowLocationCheckBox,
    setCheckedLocations,
    setDisableLocations,
    handleToggleBulkSelection,
    handleToggleGlobalSelection,
  };
}

export default useLocationBulkSelection;
